import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { preReleaseService, ProjectFeedback } from '../../services/preReleaseService';
import { Avatar } from '../common/Avatar';

interface FeedbackSectionProps {
  projectId: string;
  feedback: ProjectFeedback[];
  onFeedbackAdded: () => void;
}

export const FeedbackSection: React.FC<FeedbackSectionProps> = ({
  projectId,
  feedback,
  onFeedbackAdded
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    feedback_type: 'feature_request' as const,
    title: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSubmitting(true);
    setError(null);

    try {
      await preReleaseService.addFeedback({
        project_id: projectId,
        feedback_type: feedbackForm.feedback_type,
        title: feedbackForm.title.trim() || undefined,
        description: feedbackForm.description.trim(),
        priority_vote: 0
      });

      setFeedbackForm({
        feedback_type: 'feature_request',
        title: '',
        description: ''
      });
      setShowFeedbackForm(false);
      onFeedbackAdded();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(t('feedback.errorSubmitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'feature_request': return '💡';
      case 'improvement': return '⚡';
      case 'concern': return '⚠️';
      case 'general': return '💬';
      default: return '💬';
    }
  };

  const getFeedbackTypeLabel = (type: string) => {
    switch (type) {
      case 'feature_request': return t('feedback.featureRequest');
      case 'improvement': return t('feedback.improvement');
      case 'concern': return t('feedback.concern');
      case 'general': return t('feedback.generalFeedback');
      default: return t('feedback.generalFeedback');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="feedback-section">
      <div className="feedback-header">
        <h2>{t('feedback.communityFeedback')}</h2>
        {user && (
          <button 
            className="add-feedback-btn"
            onClick={() => setShowFeedbackForm(!showFeedbackForm)}
          >
            {showFeedbackForm ? t('feedback.cancel') : t('feedback.addFeedback')}
          </button>
        )}
      </div>

      {showFeedbackForm && (
        <div className="feedback-form-container">
          <form onSubmit={handleSubmitFeedback} className="feedback-form">
            <div className="form-group">
              <label>{t('feedback.feedbackType')}</label>
              <select
                value={feedbackForm.feedback_type}
                onChange={(e) => setFeedbackForm(prev => ({
                  ...prev,
                  feedback_type: e.target.value as any
                }))}
                required
              >
                <option value="feature_request">{t('feedback.featureRequest')}</option>
                <option value="improvement">{t('feedback.improvement')}</option>
                <option value="concern">{t('feedback.concern')}</option>
                <option value="general">{t('feedback.generalFeedback')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('feedback.titleOptional')}</label>
              <input
                type="text"
                value={feedbackForm.title}
                onChange={(e) => setFeedbackForm(prev => ({
                  ...prev,
                  title: e.target.value
                }))}
                placeholder={t('feedback.titlePlaceholder')}
              />
            </div>

            <div className="form-group">
              <label>{t('feedback.descriptionRequired')}</label>
              <textarea
                value={feedbackForm.description}
                onChange={(e) => setFeedbackForm(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
                placeholder={t('feedback.descriptionPlaceholder')}
                rows={4}
                required
              />
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setShowFeedbackForm(false)}
                className="cancel-btn"
              >
                {t('feedback.cancel')}
              </button>
              <button 
                type="submit" 
                disabled={submitting || !feedbackForm.description.trim()}
                className="submit-btn"
              >
                {submitting ? t('feedback.submitting') : t('feedback.submitFeedback')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="feedback-list">
        {feedback.length === 0 ? (
          <div className="no-feedback">
            <p>{t('feedback.noFeedbackYet')}</p>
          </div>
        ) : (
          feedback.map(item => (
            <div key={item.id} className="feedback-item">
              <div className="feedback-header-item">
                <div className="feedback-type">
                  <span className="type-icon">{getFeedbackTypeIcon(item.feedback_type)}</span>
                  <span className="type-label">{getFeedbackTypeLabel(item.feedback_type)}</span>
                </div>
                <div className="feedback-meta">
                  <div className="user-info">
                    <Avatar
                      src={item.user?.avatar_url}
                      username={item.user?.username}
                      className="user-avatar"
                      size={24}
                    />
                    <span className="username">{item.user?.username}</span>
                  </div>
                  <span className="feedback-date">{formatDate(item.created_at)}</span>
                </div>
              </div>

              {item.title && (
                <h4 className="feedback-title">{item.title}</h4>
              )}

              <div className="feedback-description">
                {item.description.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {item.priority_vote > 0 && (
                <div className="priority-votes">
                  <span className="vote-count">+{item.priority_vote} votes</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {!user && feedback.length > 0 && (
        <div className="login-prompt-feedback">
          <p>
            <a href="/login">Login</a> to add your own feedback and suggestions.
          </p>
        </div>
      )}
    </div>
  );
};